/**
 * Web Audio gain pipeline for per-track loudness normalization.
 * When normalize is off, the <audio> element plays straight to the device
 * so Android can use hardware decode (no resample/compressor).
 */
import { useEffect, useRef } from "react";
import type { Track } from "@memes/shared";
import { applyVoiceOutputMode, usePlayer } from "../state/player";
import {
  ensureAudioContextRunning,
  markWebAudioGraphAttached,
  setPlaybackAudioContext,
} from "../lib/audioGraphRuntime";

export { ensureAudioContextRunning, resumePlaybackAudioContext } from "../lib/audioGraphRuntime";

export function linearGainFromDb(db: number): number {
  return Math.pow(10, db / 20);
}

export function effectiveTrackGainDb(opts: {
  normalize: boolean;
  loudnessGainDb: number | null | undefined;
  preampDb: number;
}): number {
  if (!opts.normalize || opts.loudnessGainDb == null || !Number.isFinite(opts.loudnessGainDb)) {
    return 0;
  }
  return Math.max(-24, Math.min(24, opts.loudnessGainDb + opts.preampDb));
}

export function shouldUseWebAudioGraph(opts: {
  normalize: boolean;
  loudnessGainDb: number | null | undefined;
  voiceConnected: boolean;
  preampDb?: number;
}): boolean {
  if (opts.voiceConnected) return false;
  return (
    effectiveTrackGainDb({
      normalize: opts.normalize,
      loudnessGainDb: opts.loudnessGainDb,
      preampDb: opts.preampDb ?? 0,
    }) !== 0
  );
}

export function useAudioGraph(
  primary: HTMLAudioElement | null,
  track: Track | null,
  secondary: HTMLAudioElement | null = null,
): void {
  const normalize = usePlayer((s) => s.normalize);
  const normalizeGainDb = usePlayer((s) => s.normalizeGainDb);
  const volume = usePlayer((s) => s.volume);
  const voiceConnected = usePlayer((s) => s.voiceConnected);
  const muted = usePlayer((s) => s.muted);

  const ctxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const sourcesRef = useRef(new Map<HTMLAudioElement, MediaElementAudioSourceNode>());

  useEffect(() => {
    const live = [primary, secondary].filter((el): el is HTMLAudioElement => el != null);
    if (live.length === 0) return;

    const useGraph =
      sourcesRef.current.size > 0 ||
      shouldUseWebAudioGraph({
        normalize,
        loudnessGainDb: track?.loudnessGainDb,
        voiceConnected,
        preampDb: normalizeGainDb,
      });

    for (const el of live) {
      applyVoiceOutputMode(el, voiceConnected, volume, muted, useGraph);
    }

    if (!useGraph) return;

    if (!ctxRef.current || !gainRef.current) {
      const ctx = new AudioContext();
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      ctxRef.current = ctx;
      gainRef.current = gain;
      setPlaybackAudioContext(ctx);
    }

    const ctx = ctxRef.current;
    const gain = gainRef.current;
    if (!ctx || !gain) return;

    for (const el of live) {
      if (sourcesRef.current.has(el)) continue;
      const source = ctx.createMediaElementSource(el);
      source.connect(gain);
      sourcesRef.current.set(el, source);
      markWebAudioGraphAttached();
    }

    ensureAudioContextRunning(ctx);

    const trackGainDb = effectiveTrackGainDb({
      normalize,
      loudnessGainDb: track?.loudnessGainDb,
      preampDb: normalizeGainDb,
    });

    const userLinear = voiceConnected ? 0 : muted ? 0 : volume;
    const target = userLinear * linearGainFromDb(trackGainDb);
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(target, now + 0.08);

    return () => {
      /* graph lives for app lifetime once MediaElementSource is created */
    };
  }, [
    primary,
    secondary,
    track?.id,
    track?.loudnessGainDb,
    normalize,
    normalizeGainDb,
    volume,
    voiceConnected,
    muted,
  ]);

  useEffect(() => {
    const onVis = (): void => {
      if (document.visibilityState === "visible") {
        ensureAudioContextRunning(ctxRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
}
